import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import parse from "html-react-parser";
import { useMyHrUserRolesDataByContract } from "my-hr/hooks";
import {
  MyNewsListItemHeader,
  MyNewsLoadingComponent,
} from "my-news/components";
import { useMyNewsItem } from "my-news/hooks";
import {
  OcCardButtonBar,
  OcConfirmationDialog,
  OcPageNameTitle,
  OcPageTransition,
} from "oc/components";
import { useOcCurrentUserState, useOcIsMobile } from "oc/hooks";

export const MyNewsShowPage = () => {
  const theme = useTheme();
  let { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { t } = useTranslation();
  const isMobile = useOcIsMobile();

  const { data, loading, error, removeMyNewsItem } = useMyNewsItem({
    _id: id,
  });

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  let { contractCode } = useOcCurrentUserState();

  const { hasRole } = useMyHrUserRolesDataByContract({
    contract: contractCode,
  });

  let hasEditorRight = hasRole(["MyNews.editor"], []);

  const handleButtonClick = () => {
    history.goBack();
  };

  const handleEditMyNews = () => {
    history.push(`/my-news/edit/${id}`);
  };

  const handleRemoveMyNews = () => {
    setShowConfirmDialog(false);
    removeMyNewsItem({ variables: { _id: id } });
    history.goBack();
  };

  const handleDialogCancel = () => {
    setShowConfirmDialog(false);
  };

  const isOwnNews = contractCode === data?.createdBy;

  const isBorder =
    data?.priority === "high"
      ? `2px solid ${theme.palette.yellowBase.main}`
      : "unset";

  return (
    <OcPageTransition>
      <OcConfirmationDialog
        showDialog={showConfirmDialog}
        onClose={() => {
          handleRemoveMyNews();
        }}
        onCancel={handleDialogCancel}
        title={t("OC_DELETE_CONFIRM")}
        texts={[t("MY_NEWS_DELETE_CONFIRM_INFO")]}
        okButtonText={t("OC_ALL_RIGHT")}
        cancelButtonText={t("OC_CANCEL")}
      />
      <OcPageNameTitle title="MyNews" goBack={true} />
      <Box padding={isMobile ? "0px 0px 16px 0px" : "0px 16px 16px 16px"}>
        {loading && !error ? (
          <>
            <MyNewsLoadingComponent isMobile={isMobile} />
          </>
        ) : (
          <Box
            id="news-card"
            style={{
              padding: "0px 16px 16px 16px",
              borderRadius: "4px",
              backgroundColor: theme.palette.white.main,
              border: isBorder,
            }}
          >
            <MyNewsListItemHeader item={data} />
            <Box height="16px" />
            <Typography variant="body1">{data?.description}</Typography>
            <Box>{parse(data?.content ?? "")}</Box>
            <OcCardButtonBar
              handleLeftButton={() => setShowConfirmDialog(true)}
              leftButtonColor={hasEditorRight ? "primary" : undefined}
              leftButtonText={t("OC_DELETE")}
              leftButtonDisabled={loading || !isOwnNews}
              handleRightButton={handleButtonClick}
              rightButtonText={t("OC_BACK")}
              rightButtonId="cancel-button"
              rightButtonColor="secondary"
              rightButtonDisabled={loading}
              handlePrimaryButton={handleEditMyNews}
              primaryButtonText={hasEditorRight ? t("MY_NEWS_EDITING") : undefined}
              primaryButtonDisabled={loading || !isOwnNews}
              primaryButtonId="edit-button"
            />
          </Box>
        )}
      </Box>
    </OcPageTransition>
  );
};

export default MyNewsShowPage;
